import * as React from 'react'

import * as core from '@boutique/services'
import * as entities from '@boutique/entities'

import * as hooks from '@owl-nest/hooks'
import { useQueryParams, type UseQueryParams } from '@boutique/hooks'
import * as url from '@boutique/url'

export const HITS_PER_PAGE = 36

type UseProductListing = UseQueryParams<entities.search.product.Query> & {
  response: hooks.Response<entities.search.product.Result>
  persistedData: entities.search.product.Result | undefined
  resetFilters: () => entities.search.product.Query
  setFilter: <FILTER extends keyof entities.search.product.Filters>(
    key: FILTER,
    value: entities.search.product.Filters[FILTER],
  ) => entities.search.product.Query
  setSort: (sort?: entities.search.Sort) => entities.search.product.Query
  setPage: (page?: number) => entities.search.product.Query
  setPriceRange: (range?: { start?: number; end?: number }) => entities.search.product.Query
  filterCount: number
}

export function useProductListing(): UseProductListing {
  const { apply, href, query } = useQueryParams(
    url.products({}).path,
    entities.search.product.fromParsedUrlQuery,
    entities.search.product.toUrlSearchParams,
  )

  const { response } = core.search.product.useProductSearch(
    {
      ...query,
      hitsPerPage: HITS_PER_PAGE,
    },
    {
      fetchOnMount: 'force',
    },
  )

  const persistedDataRef = React.useRef(response.data)
  if (response.data !== undefined) {
    persistedDataRef.current = response.data
  }

  return {
    query,
    persistedData: persistedDataRef.current,
    response,
    apply,
    href,
    resetFilters: () => entities.search.product.resetFilters(query),
    setFilter: (key, value) => entities.search.product.setFilter(query, key, value),
    setSort: (sort) => entities.search.setSort(query, sort),
    setPage: (page) => entities.search.setPage(query, page),
    setPriceRange: (range) => entities.search.setPriceRange(query, range),
    filterCount: entities.search.product.getFilterCount(query),
  }
}
